import {
  DataGridPro,
  GridColDef,
  GridRowModel,
  GridRowSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { getFontCss, styleConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import {
  base_colorAction,
  base_colorRaisedSurface,
  base_contentSelected,
  cta_linkTextAlert,
  notification_colorAlert,
  tag_colorTagHighlightText,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_family_weight_regular } from 'ext-ui/style/typography'
import moment from 'moment'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { ExpenseReportContext } from 'src/app/ExpenseReport/ExpenseReport'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import { renderHeader } from 'src/components/DataGrid/cells/DataGridHeaderCell/DataGridHeaderCell'
import IconsCell from 'src/components/DataGrid/cells/IconsCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import ReceiptsTooltip from 'src/components/ReceiptsTooltip/ReceiptsTooltip'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlag from 'src/components/Svgs/SvgFlag'
import SvgNewCheck from 'src/components/Svgs/SvgNewCheck'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import Crncy from 'src/localization/Crncy'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import {
  ExpenseManagerReviewStatus,
  ExpenseModel,
  ExpenseReportStatus,
  ExpenseResponse,
  ExpenseStatus,
  ExpenseType,
} from 'src/models/expenseReport.model'
import { rollupStatusFromAttachments } from 'src/models/receiptVerification.models'
import { connectedPush } from 'src/navigation/Lnk'
import { updateExpenseUrl } from 'src/navigation/urls'
import { usePatchReimbursementManagerReview } from 'src/queries/expenseReview.queries'
import {
  selectNullSafeLocaleFromState,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { toDollars } from 'src/utils/currency.utils'
import styled, { css } from 'styled-components'

type ExpensesDataGridProps = {
  expenses: ExpenseResponse[]
  expenseReportStatus: ExpenseReportStatus
  bulkSelected: boolean
  selectedExpenses: string[]
  handleExpenseCheckboxClick: (option: string) => void
  handleBulkExpenseCheckboxClick: () => void
  showCheckbox: boolean
  isDraftStatus: boolean
  context: ExpenseReportContext
}

interface OOPEReportDataGridRowModel extends GridValidRowModel {
  expense: ExpenseModel
  index: number
}

const StyledSpanLabel = styled.span`
  display: inline-block;
  text-align: center;
  border-radius: 5px;
  padding: 4px;
  white-space: nowrap;
  /* {getBreakpointDown(breakpointSizes.MD)(css
    position: absolute;
  )} */
`

// display: flex;
// justify-content: center;
// align-items: center;
// border-radius: 5px;
// padding: 2px 4px;
// background-color: ${({ backgroundColor }): string => backgroundColor};

export const getAccountingManagerReviewIcon = (
  status: ExpenseManagerReviewStatus | undefined,
): React.ReactElement => {
  switch (status) {
    case ExpenseManagerReviewStatus.Reviewed:
      return (
        <SvgNewCheck
          stroke={tag_colorTagHighlightText}
          data-testid={'reviewed'}
          css={css`
            scale: calc(24 / 16);
          `}
        />
      )
    case ExpenseManagerReviewStatus.Rejected:
      return (
        <SvgFlag
          stroke={notification_colorAlert}
          data-testid={'flagged'}
          css={css`
            scale: calc(22 / 16);
          `}
        />
      )
    default:
      return (
        <SvgClock
          data-testid={'pendingReviewIcon'}
          css={css`
            scale: calc(22 / 20);
          `}
        />
      )
  }
}

// TODO steph - should this be reformatted into a PillCell?
const ExpenseStatusLabel: React.FC<{ status: ExpenseStatus }> = ({
  status,
}) => {
  const translate = useTranslate()

  switch (status) {
    case ExpenseStatus.Pending: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.black,
              styleConstants.fontFamilies.medium,
            )}
            background-color: ${styleConstants.colors.gray_medium};
          `}
        >
          {translate('Pending Approval')}
        </StyledSpanLabel>
      )
    }
    case ExpenseStatus.Approved: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.green_darkest,
              styleConstants.fontFamilies.medium,
            )}
            background-color: ${styleConstants.colors.green_light};
          `}
        >
          {translate('Approved')}
        </StyledSpanLabel>
      )
    }
    case ExpenseStatus.Rejected: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.red_dark,
              styleConstants.fontFamilies.medium,
            )};
            background-color: #fad3d6;
          `}
        >
          {translate('Rejected')}
        </StyledSpanLabel>
      )
    }
    default: {
      return <span>{'--'}</span>
    }
  }
}

const ExpensesDataGrid: React.FC<ExpensesDataGridProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useDispatch()

  const userId = useSelector(selectUserIdFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const { mutate: patchReimbursementManagerReview } =
    usePatchReimbursementManagerReview()
  const { analytics } = useAnalyticsContext()

  const NoResultsMessage = (): ReactElement => (
    <div
      css={css`
        padding-top: 40px;
        height: 500px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
    >
      {translateSingleString(
        locale,
        'No results found. Please check your filters and try again.',
      )}
    </div>
  )

  const rows: OOPEReportDataGridRowModel[] = props.expenses.map(
    (expense, i) => {
      return {
        id: expense.id,
        expense: expense,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<OOPEReportDataGridRowModel> | undefined> = [
    {
      field: 'date',
      headerName: translateSingleString(locale, 'Date'),
      groupable: false,
      minWidth: 150,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expense = params.row.expense
        return (
          <DataGridCell
            bold
            text={
              expense.expenseDate
                ? moment(expense.expenseDate).format('l')
                : '--'
            }
            params={params}
          />
        )
      },
    },
    {
      field: 'expense_name',
      headerName: translateSingleString(locale, 'Expense Name'),
      flex: 1,
      minWidth: 150,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expense = params.row.expense
        const showAlert =
          props.expenseReportStatus === ExpenseReportStatus.Draft &&
          (expense.notes === undefined || // require notes on all expenses
            // require receipts on non mileage expenses
            (expense.receiptAttachments !== undefined &&
              expense.receiptAttachments.length === 0 &&
              expense.type !== ExpenseType.Mileage))
        return (
          <DataGridCell
            text={expense.expenseName}
            icon={showAlert ? <SvgAlertCircledFilled /> : undefined}
            color={showAlert ? 'severe' : undefined}
            params={params}
          />
        )
      },
    },
    {
      field: 'amount',
      headerName: translateSingleString(locale, 'Amount'),
      flex: 1,
      minWidth: 100,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expense = params.row.expense
        return (
          <DataGridCell
            text={
              expense.currencyCode &&
              expense.amountCents && (
                <Crncy
                  currency={expense.currencyCode}
                  amount={toDollars(expense.amountCents)}
                />
              )
            }
            params={params}
          />
        )
      },
    },
    {
      field: 'receipts',
      headerName: translateSingleString(locale, 'Receipts'),
      flex: 1,
      minWidth: 100,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: renderHeader,
      renderCell: (params): React.ReactElement => {
        const expense = params.row.expense
        let icon
        // TODO steph - make this temporary fix more permanent
        // to avoid tooltip being hidden by manager's hover actions, open to the other side
        const placement =
          props.context === ExpenseReportContext.managerReview
            ? 'left'
            : 'right'
        if (
          expense.receiptAttachments !== undefined &&
          expense.receiptAttachments.length > 0
        ) {
          icon = (
            <ReceiptsTooltip
              count={expense.receiptAttachments.length}
              placement={placement}
              verificationStatus={rollupStatusFromAttachments(
                expense.receiptAttachments,
              )}
            />
          )
        }
        if (
          props.expenseReportStatus === ExpenseReportStatus.Draft &&
          expense.receiptAttachments !== undefined &&
          expense.receiptAttachments.length === 0 &&
          expense.type !== ExpenseType.Mileage
        ) {
          icon = <ReceiptsTooltip placement={placement} isMissing />
        }
        return <IconsCell icon={icon} params={params} />
      },
    },
    props.context === ExpenseReportContext.managerReview
      ? {
          field: 'approval_status',
          headerName: translateSingleString(locale, 'Review Status'),
          minWidth: 150,
          sortable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            const expense = params.row.expense
            let icon
            switch (expense.managerReviewStatus) {
              case ExpenseManagerReviewStatus.Reviewed:
                icon = (
                  <SvgNewCheck
                    stroke={tag_colorTagHighlightText}
                    data-testid={'reviewed'}
                    css={css`
                      scale: calc(24 / 16);
                    `}
                  />
                )
                break
              case ExpenseManagerReviewStatus.Rejected:
                icon = (
                  <SvgFlag
                    stroke={notification_colorAlert}
                    data-testid={'flagged'}
                    css={css`
                      scale: calc(22 / 16);
                    `}
                  />
                )
                break
            }
            if (icon !== undefined) {
              return <IconsCell icon={icon} params={params} />
            }
            return (
              <PillCell color={'pending'} params={params}>
                {translateSingleString(locale, 'Pending Review')}
              </PillCell>
            )
          },
        }
      : undefined,
    props.context === ExpenseReportContext.accounting
      ? {
          field: 'manager_approval_status',
          headerName: translateSingleString(locale, 'Manager Review'),
          minWidth: 150,
          sortable: false,
          hideable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            const expense = params.row.expense
            return (
              <IconsCell
                icon={getAccountingManagerReviewIcon(
                  expense.managerReviewStatus,
                )}
                params={params}
              />
            )
          },
        }
      : undefined,
    props.context !== ExpenseReportContext.managerReview
      ? {
          field: 'status',
          headerName: translateSingleString(
            locale,
            props.context === ExpenseReportContext.accounting
              ? 'Final Approval'
              : 'Status',
          ),
          flex: 1,
          minWidth: 150,
          hideable: false,
          sortable: false,
          disableColumnMenu: true,
          renderHeader: renderHeader,
          renderCell: (params): React.ReactElement => {
            return (
              <DataGridCell
                text={
                  props.isDraftStatus ? (
                    '--'
                  ) : (
                    <ExpenseStatusLabel status={params.row.expense.status} />
                  )
                }
                params={params}
              />
            )
          },
        }
      : undefined,
    props.context === ExpenseReportContext.managerReview
      ? {
          field: 'hover-actions',
          headerName: translateSingleString(locale, 'Actions'),
          resizable: false,
          sortable: false,
          hideable: false,
          maxWidth: 1,
          disableColumnMenu: true,
          cellClassName: 'hover-actions',
          headerClassName: 'hideRightSeparator hideHeader',
          renderHeader: () => <></>,
          renderCell: (params: GridRowModel): React.ReactElement => {
            return (
              <div
                css={css`
                  display: flex;
                  gap: ${spacing(2)};
                  padding: ${spacing(1)};
                  align-items: center;
                  border-radius: ${spacing(2)};
                  background: ${base_colorRaisedSurface};

                  .no-shell {
                    font-family: ${font_family_weight_regular};
                    padding: ${spacing(2)} ${spacing(4)};
                    &:hover {
                      background-color: ${base_contentSelected};
                    }
                  }
                `}
              >
                <Button
                  variant={'no-shell'}
                  dropShadow={false}
                  iconLeft={
                    <SvgNewCheck
                      stroke={base_colorAction}
                      data-testid={'review'}
                    />
                  }
                  onClick={(e): void => {
                    e.stopPropagation()
                    patchReimbursementManagerReview({
                      body: {
                        reimbursementIds: [params.row.expense.id],
                        managerApprovalStatus:
                          ExpenseManagerReviewStatus.Reviewed,
                      },
                    })
                    analytics?.track('manager review actioned reimbursement', {
                      action: 'approved',
                    })
                  }}
                >
                  {translate('Review')}
                </Button>
                <Button
                  variant={'no-shell'}
                  dropShadow={false}
                  iconLeft={<SvgFlag stroke={notification_colorAlert} />}
                  style={{
                    color: cta_linkTextAlert,
                  }}
                  onClick={(e): void => {
                    e.stopPropagation()
                    patchReimbursementManagerReview({
                      body: {
                        reimbursementIds: [params.row.expense.id],
                        managerApprovalStatus:
                          ExpenseManagerReviewStatus.Rejected,
                      },
                    })
                    analytics?.track('manager review actioned reimbursement', {
                      action: 'rejected',
                    })
                  }}
                >
                  {translate('Reject')}
                </Button>
              </div>
            )
          },
        }
      : undefined,
    chevronColumnDef,
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter((column) => column !== undefined) as Array<
        GridColDef<OOPEReportDataGridRowModel>
      >,
    [columns],
  )

  // custom event implementations from https://mui.com/x/react-data-grid/events/#catalog-of-events
  const apiRef = useGridApiRef()
  useEffect(() => {
    const unsubscribe = apiRef.current?.subscribeEvent(
      'headerSelectionCheckboxChange',
      props.handleBulkExpenseCheckboxClick,
    )

    return () => {
      unsubscribe?.()
    }
  }, [apiRef?.current, props.handleBulkExpenseCheckboxClick])

  // calculate window height to properly set the table height
  // table height must be set for the infinite scroll to work properly
  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)
  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const checkboxSelectionEnabled = useCallback(() => {
    switch (props.context) {
      case ExpenseReportContext.myWallet:
        return props.expenseReportStatus === ExpenseReportStatus.Draft
      case ExpenseReportContext.managerReview:
        return organization?.expenseApprover?.id === userId
      case ExpenseReportContext.accounting:
        return organization?.expenseApprover?.id === userId
    }
  }, [props.context, props.expenseReportStatus, organization, userId])

  return (
    <div
      ref={ref}
      css={css`
        width: 100%;
        height: ${(clientHeight || 0) - 60}px;
      `}
    >
      {/* TODO: this isn't using our pre-styled datagrid */}
      {/* when moved over, remove sx, clean up pinned columns, remove `renderHeader` from colDef */}
      <DataGridPro
        apiRef={apiRef}
        initialState={{
          pinnedColumns: {
            right: ['hover-actions', 'chevron'],
            left: ['__check__', 'date', 'expense_name'],
          },
        }}
        rows={rows}
        columns={columnsSafe}
        sx={{
          // sticky column styling
          '.MuiDataGrid-cell': {
            backgroundColor: '#ffffff',
            overflow: 'visible',
            padding: 0,
          },
          '.MuiDataGrid-filler': {
            backgroundColor: '#ffffff',
          },
          // hide border on pinned right column
          // hide padding on pinned right column (chevron cell sets its own border)
          '.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-filler--pinnedRight': {
            paddingLeft: '0px',
            borderLeft: 'unset',
          },
          // remove left border on sticky column header
          '& .MuiDataGrid-columnHeader': {
            borderLeftStyle: 'unset',
          },
          // defining a class to hide right border on column header
          '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          // hide border on pinned left column
          '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-filler--pinnedLeft': {
            borderRight: 'unset',
          },
          // row hover color
          '.MuiDataGrid-row:hover': {
            '.MuiDataGrid-cell, .MuiDataGrid-cell--pinnedRight, .MuiDataGrid-cell--pinnedLeft':
              {
                backgroundColor: '#f5f5f5',
              },
          },

          // hide column header
          '.MuiDataGrid-columnHeader .hideHeader': {
            width: '0px',
            display: 'none',
            padding: '0px',
          },
          // show action buttons on hover (add 'hover-actions' to the affected column's cellClassName property)
          '.MuiDataGrid-row': {
            '.hover-actions': {
              display: 'none',
              right: '0px',
              justifyContent: 'right',
            },
            '&:hover > .hover-actions': {
              display: 'flex',
            },
          },
          // Lower the scrollbar so it is below modals
          '.MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
        }}
        rowHeight={DataGridRowHeight.Default}
        slots={{
          loadingOverlay: () => <></>,
          noResultsOverlay: () => <NoResultsMessage />,
          noRowsOverlay: () => (
            <TableEmptyState
              design={TableEmptyStateDesigns.ExpensesNeedsApproval}
            />
          ),
        }}
        // row selection logic
        checkboxSelection={checkboxSelectionEnabled()} // fast follow? setup manager view to allow bulk review
        isRowSelectable={(): boolean => checkboxSelectionEnabled()}
        onRowSelectionModelChange={(selection: GridRowSelectionModel): void => {
          if (checkboxSelectionEnabled()) {
            // this is stupid but hopefully temporary
            const newlySelectedId = [...selection.ids].filter(
              (id: string | number) =>
                !props.selectedExpenses?.some(
                  (expenseId: string) => expenseId === id,
                ),
            )?.[0]
            const newlySelected = props.expenses?.find(
              (expense: ExpenseModel) => expense.id === newlySelectedId,
            )
            const newlyUnselected = props.selectedExpenses?.filter(
              (expenseId: string) => !selection.ids.has(expenseId),
            )?.[0]
            if (newlySelected !== undefined) {
              props.handleExpenseCheckboxClick(newlySelected.id)
            }
            if (newlyUnselected !== undefined) {
              props.handleExpenseCheckboxClick(newlyUnselected)
            }
          }
        }}
        onRowClick={(params, event): void => {
          if (event.defaultPrevented) return
          const expense = params.row.expense
          dispatch(
            connectedPush({
              pathname: updateExpenseUrl(expense.id),
              search: '',
              hash: '',
              state: {},
            }),
          )
        }}
      />
    </div>
  )
}

export default ExpensesDataGrid
